import { CityAndCounty } from "../../../common/types/apiTypes";

export interface JobAd {
  version?: number;
  creationDate?: string | number | Date;
  id: string;
  bundleId?: string;
  title: string;
  orderTitle?: string;
  status: JobAdStatus;
  startDate: string;
  endDate: string;
  locations: CityAndCounty[];
  jobDescription: {
    finnish: string;
    english: string;
  };
  contactPerson: {
    id?: string;
    name: string;
    phone: string;
    email: string;
  };
  fieldOfWorkId: string;
  workTitleId: string;
  workTimeType: WorkTimeType;
  employmentType: EmploymentType;
  salary?: string;
  remoteWork?: boolean | null;
  publishingSites?: JobAdIntegrationType[];
  customerCompanyName?: string;
  employer: string;
  campaigns?: string[];
  integrations?: JobAdIntegration[];
  totalApplicants: number;
  newApplicants?: number;
}

export interface JobAdListItem {
  id: string;
  creationDate: string;
  orderTitle?: string;
  title: string;
  locationCity: string;
  startDate: string;
  endDate: string;
  customer?: string;
  employer?: string;
  publishingSites: string;
  contactPersonName: string;
  state: string;
  totalApplicants: string;
  newApplicants?: string;
  campaigns: string[];
}

export enum JobAdIntegrationType {
  Eezy = "Eezy",
  Jobly = "Jobly",
  Duunitori = "Duunitori",
  Tyomarkkinatori = "Tyomarkkinatori",
}

export interface JobAdIntegration {
  version?: number;
  id: string;
  integration: JobAdIntegrationDetails;
}

export type JobAdIntegrationDetails =
  | EezyIntegration
  | JoblyIntegration
  | DuunitoriIntegration
  | TyomarkkinatoriIntegration;

export interface EezyIntegration {
  jobAdId: string;
  hideCustomer: boolean;
  customerDescription?: string;
  logo?: string;
  alternativeApplicationUrl?: string;
  training?: boolean;
  temporaryWork?: boolean;
  type?: JobAdIntegrationType;
  status?: PlatFormStatus;
  platformJobAdId?: string;
}

export interface JoblyIntegration {
  jobAdId: string;
  jobCategory: JobCategory;
  contractType: ContractType;
  occupationCategory: string;
  jobExperienceLevel?: JobExperienceLevel;
  type?: JobAdIntegrationType;
  status?: PlatFormStatus;
  platformJobAdId?: string;
}

export interface DuunitoriIntegration {
  jobAdId: string;
  type?: JobAdIntegrationType;
  status?: PlatFormStatus;
  platformJobAdId?: string;
}

export interface TyomarkkinatoriIntegration {
  jobAdId: string;
  durationOfTemporaryEmployment?: DurationOfTemporaryEmployment | "";
  ESCO: string[];
  numberOfJobPositions: number;
  salaryType: SalaryType;
  workingLanguages: string[];
  assignment: Assignment;
  commissionCompanyName: string;
  discloseCommissionCompany: boolean;
  jobDescription?: {
    finnish: string;
    english?: string;
  } | null;
  noteEURES?: {
    EURESinstructionsFinnish: string;
    EURESinstructionsEnglish: string;
  } | null;
  type?: JobAdIntegrationType;
  status?: PlatFormStatus;
  platformJobAdId?: string;
}

export type SectionProps = {
  isEditing: boolean;
  onPublishSelection?: (integration: JobAdIntegrationType) => void;
  integrationsToPublish?: JobAdIntegrationType[];
  selectedJobAd?: JobAd;
};

export enum JobCategory {
  FULL_TIME = "FULL_TIME",
  PART_TIME = "PART_TIME",
  HOURLY_WORK = "HOURLY_WORK",
  OTHER = "OTHER",
  GIG_WORK = "GIG_WORK",
  EVENING_WORK = "EVENING_WORK",
  NIGHT_WORK = "NIGHT_WORK",
  WEEKEND_WORK = "WEEKEND_WORK",
  APPRENTICESHIP = "APPRENTICESHIP",
}

export enum ContractType {
  PERMANENT_POSITION = "PERMANENT_POSITION",
  FIXED_TERM_POSITION = "FIXED_TERM_POSITION",
  PERMANENT_EMPLOYMENT = "PERMANENT_EMPLOYMENT",
  FIXED_TERM_EMPLOYMENT = "FIXED_TERM_EMPLOYMENT",
  SUMMER_JOB = "SUMMER_JOB",
  INTERNSHIP = "INTERNSHIP",
  SUBSTITUTE = "SUBSTITUTE",
  FREELANCER = "FREELANCER",
  OTHER = "OTHER",
}

// PRODUCTION_AND_SMALL_SCALE_INDUSTRY = 148,

export enum JobExperienceLevel {
  INTERN,
  EMPLOYEE,
  SPECIALIST,
  SUPERVISOR,
  MANAGER,
}

export enum SalaryType {
  HOURLY_WAGE,
  FIXED_SALARY,
  COMMISSION_BASED_SALARY,
  FIXED_SALARY_PLUS_COMMISSION,
  HOURLY_WAGE_PLUS_COMMISSION,
  CONTRACT_BASED_SALARY,
  OTHER,
}

export enum Assignment {
  TEMPORARY_JOB,
  RECRUITMENT_ASSIGNMENT,
}

export enum WorkTimeType {
  FULL_TIME = "FULL_TIME",
  PART_TIME = "PART_TIME",
  VARIABLE_TIME = "VARIABLE_TIME",
}

export enum EmploymentType {
  TEMPORARY = "TEMPORARY",
  INDEFINITE = "INDEFINITE",
  SUMMER_WORK = "SUMMER_WORK",
  SEASONAL_WORK = "SEASONAL_WORK",
}

export enum JobAdStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  CLOSED = "CLOSED",
  EXPIRED = "EXPIRED",
}

export enum DurationOfTemporaryEmployment {
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
}

export enum PlatFormStatus {
  PENDING = "PENDING", // Waiting for publishing
  PUBLISHED = "PUBLISHED", // Published
  DELETED = "DELETED", // Removed
  FAILED_TO_PUBLISH = "FAILED_TO_PUBLISH", // Publishing failed
  FAILED_TO_UPDATE = "FAILED_TO_UPDATE", // Update failed
  FAILED_TO_DELETE = "FAILED_TO_DELETE", // Deletion failed
}

const QUEUE_OWN = { id: "own", name: "queueOwn" };
const QUEUE_OTHER = { id: "other", name: "queueOther" };

export const QUEUE_TYPES = [QUEUE_OWN, QUEUE_OTHER];

export const STATUS_TYPES = [
  { id: JobAdStatus.DRAFT, name: JobAdStatus.DRAFT },
  { id: JobAdStatus.PUBLISHED, name: JobAdStatus.PUBLISHED },
  { id: JobAdStatus.CLOSED, name: JobAdStatus.CLOSED },
  { id: JobAdStatus.EXPIRED, name: JobAdStatus.EXPIRED },
];
